/**
 * ClientDetailPage - Page for coaches to manage notes for a client
 * Workout viewing is now done through FitTrack with client switcher
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  MessageSquare,
  Loader2,
  Edit2,
  Trash2,
  Pin,
  Dumbbell,
  TrendingUp,
} from 'lucide-react';
import { useCoachStore, NOTE_TYPES } from '../../stores/coachStore';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddNoteModal from '../../components/coach/AddNoteModal';

const ClientDetailPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const {
    clients,
    loadClientNotes,
    clientNotes,
    openAddNoteModal,
    isAddNoteModalOpen,
    deleteNote,
    setViewingClient,
  } = useCoachStore();

  const [isLoadingNotes, setIsLoadingNotes] = useState(true);

  // Find client info
  const client = clients.find((c) => c.client_id === clientId);
  const clientName = client?.client?.email?.split('@')[0] || 'Öğrenci';

  // Load notes
  useEffect(() => {
    if (clientId) {
      setIsLoadingNotes(true);
      loadClientNotes(clientId).finally(() => setIsLoadingNotes(false));
    }
  }, [clientId, loadClientNotes]);

  const notes = clientNotes[clientId] || [];

  const handleDeleteNote = async (noteId) => {
    if (confirm('Bu notu silmek istediğinize emin misiniz?')) {
      try {
        await deleteNote(noteId, clientId);
      } catch (error) {
        // Error handled in store
      }
    }
  };

  // Navigate to FitTrack with client param
  const handleViewFitTrack = () => {
    setViewingClient(clientId, {
      id: clientId,
      email: client?.client?.email,
      display_name: clientName,
    });
    navigate(`/fittrack?client=${clientId}`);
  };

  // Navigate to Analytics with client param
  const handleViewAnalytics = () => {
    setViewingClient(clientId, {
      id: clientId,
      email: client?.client?.email,
      display_name: clientName,
    });
    navigate(`/fittrack/analytics?client=${clientId}`);
  };

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Öğrenci bulunamadı</p>
          <button
            onClick={() => navigate('/coach')}
            className="text-orange-400 hover:text-orange-300"
          >
            Geri dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/coach')}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold">{clientName}</h1>
                <p className="text-sm text-gray-400">{client.client?.email}</p>
              </div>
            </div>

            <motion.button
              onClick={() => openAddNoteModal({ clientId })}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Not Ekle
            </motion.button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <motion.button
            onClick={handleViewFitTrack}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-4 p-5 bg-gray-900/50 border border-gray-800 hover:border-blue-500/50 rounded-xl transition-colors text-left"
          >
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
              <Dumbbell className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="font-semibold">Antrenmanları Görüntüle</p>
              <p className="text-sm text-gray-400">FitTrack'te antrenman geçmişini gör</p>
            </div>
          </motion.button>

          <motion.button
            onClick={handleViewAnalytics}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-4 p-5 bg-gray-900/50 border border-gray-800 hover:border-purple-500/50 rounded-xl transition-colors text-left"
          >
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="font-semibold">Analitikleri Görüntüle</p>
              <p className="text-sm text-gray-400">İlerleme ve istatistikler</p>
            </div>
          </motion.button>
        </div>

        {/* Notes Section */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-400" />
              Notlarım
              {notes.length > 0 && (
                <span className="text-sm text-gray-400">({notes.length})</span>
              )}
            </h2>
          </div>

          <div className="p-4">
            {isLoadingNotes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 mb-3">Henüz not eklemediniz</p>
                <button
                  onClick={() => openAddNoteModal({ clientId })}
                  className="text-orange-400 hover:text-orange-300 text-sm"
                >
                  İlk notunuzu ekleyin
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    onDelete={() => handleDeleteNote(note.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Add Note Modal */}
      <AddNoteModal clientId={clientId} />
    </div>
  );
};

// Note Card Component
const NoteCard = ({ note, onDelete }) => {
  const noteType = NOTE_TYPES[note.note_type] || NOTE_TYPES.feedback;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/50 border border-gray-700 rounded-lg p-4"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{noteType.emoji}</span>
          <span className={`text-xs px-2 py-0.5 rounded bg-${noteType.color}-500/10 text-${noteType.color}-400`}>
            {noteType.label}
          </span>
          {note.is_pinned && (
            <Pin className="w-3 h-3 text-orange-400" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-700 rounded transition-colors">
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-300 whitespace-pre-wrap">{note.content}</p>

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          {note.workout_date
            ? format(parseISO(note.workout_date), 'd MMM yyyy', { locale: tr })
            : 'Genel not'}
        </span>
        <span>
          {format(parseISO(note.created_at), 'd MMM, HH:mm', { locale: tr })}
        </span>
      </div>
    </motion.div>
  );
};

export default ClientDetailPage;
